import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import RightArrow from "src/assets/svg-icons/aggregators/right_arrow.svg";
import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { useTranslation } from "react-i18next";
import InventoryIcon from "src/assets/svg-icons/side-bar/inventory.svg";
import DeleteIcon from "src/assets/svg-icons/aggregators/delete.svg";
import * as yup from "yup";
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import { useGetBranches } from "src/api/generic";
import { useGetLevelsList } from "src/api/inventory/levels";
import {
  useAddProductRecipeMutation,
  useGetMenuProductById,
  useGetProductRecipeById,
  useUpdateProductRecipeMutation,
} from "src/api/inventory/menu";
import CalcItem from "../../all/product-recipe/calculation-item";

const sellMethods = [
  {
    label: "delivery",
    value: "delivery",
  },
  {
    label: "pickup",
    value: "pickup",
  },
  {
    label: "driveThru",
    value: "drive_through",
  },
  {
    label: "dineIn",
    value: "dine_in",
  },
];

const schema = yup.object().shape({
  branch_id: yup.string().required("required"),
  // recipe_quantity: yup.number().required("required"),
  added_items: yup.array().of(
    yup.object().shape({
      item_id: yup.number().required("required"),
      item_name: yup.string(),
      item_sku: yup.string(),
      item_unit: yup.string(),
      item_unit_cost: yup.number(),
      item_quantity: yup.number().typeError("Required").required("required"),
      include_in: yup
        .array()
        .of(yup.string().required())
        .required()
        .min(1, "Required"),
      // calculations
      total: yup.number(),
    })
  ),
  removed_items: yup.array().of(
    yup.object().shape({
      item_id: yup.number().required("required"),
      item_name: yup.string(),
      item_sku: yup.string(),
      item_unit: yup.string(),
      item_unit_cost: yup.number(),
      item_quantity: yup.number().typeError("Required").required("required"),
      include_in: yup
        .array()
        .of(yup.string().required())
        .required()
        .min(1, "Required"),
      // calculations
      total: yup.number(),
    })
  ),
});

export interface FormInputs extends yup.InferType<typeof schema> {}

const ModifierRecipe = () => {
  const navigate = useNavigate();
  const { t: generalT } = useTranslation("general");
  const { t } = useTranslation("inventory");
  const { modifierId, recipeId } = useParams();
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [selectedRemovedItem, setSelectedRemovedItem] = useState<any>(null);
  const [inputValue, setInputValue] = useState<string>("");
  const [removedInputValue, setRemovedInputValue] = useState<string>("");
  const mode = !recipeId ? "add" : "edit";
  console.log({ mode });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      added_items: [],
      removed_items: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    name: "added_items",
    control,
  });

  const {
    fields: removedItemsFields,
    append: appendRemovedItem,
    remove: removeRemovedItem,
  } = useFieldArray({
    name: "removed_items",
    control,
  });

  // APIS
  const {
    data: productData,
    status: getProByIdStatus,
    refetch,
    isFetching,
    isLoading,
  } = useGetMenuProductById({
    enabled: !!modifierId,
    id: modifierId as string,
  });

  const {
    data: recipeData,
    status: getRecipeStatus,
    refetch: refetchRecipe,
    isFetching: isFetchingRecipe,
    isLoading: isLoadingRecipe,
  } = useGetProductRecipeById({
    enabled: !!recipeId,
    id: recipeId as string,
  });

  const { data: itemsData, isFetching: isFetchingItems } = useGetLevelsList({
    branch_id: +watch("branch_id"),
  });

  const { data: branchesData } = useGetBranches();
  const { mutate, error, status, isPending } = useAddProductRecipeMutation();
  const {
    mutate: mutateUpdate,
    error: errorUpdate,
    status: statusUpdate,
    isPending: isPendingUpdate,
  } = useUpdateProductRecipeMutation();

  // permissions
  // const confirmPermission = specificBranchHasPermission(
  //   InventoryPermissions.confirmStocktaking,
  //   productData?.data.branch.id
  // );
  // const addPermission = specificBranchHasPermission(
  //   InventoryPermissions.addStocktaking,
  //   productData?.data.branch.id
  // );

  const enabledBranches =
    branchesData?.data.filter((branch) => branch.status) || [];

  const navigateToInventory = () => {
    navigate("/menu/modifiers");
  };

  // Handler function when item is selected
  const handleItemSelect = (event: React.SyntheticEvent, newValue: any) => {
    setSelectedItem(newValue);
    if (newValue) {
      append({
        item_id: newValue.id,
        item_name: newValue.name,
        item_sku: newValue.sku,
        item_unit: newValue.unit?.name,
        item_quantity: 0,
        total: 0,
        item_unit_cost: newValue.unit_cost,
        include_in: ["delivery", "pickup", "drive_through", "dine_in"],
      });
      setInputValue("");
      setSelectedItem(null);
    }
  };

  // Handler function when modifier is selected
  const handleRemovedItemSelect = (
    event: React.SyntheticEvent,
    newValue: any
  ) => {
    setSelectedItem(newValue);
    if (newValue) {
      appendRemovedItem({
        item_id: newValue.id,
        item_name: newValue.name,
        item_sku: newValue.sku,
        item_unit: newValue.unit?.name,
        item_quantity: 0,
        total: 0,
        item_unit_cost: newValue.unit_cost,
        include_in: ["delivery", "pickup", "drive_through", "dine_in"],
      });
      setRemovedInputValue("");
      setSelectedRemovedItem(null);
    }
  };

  // Handle manual changes to the input field of items
  const handleInputChange = (
    event: React.SyntheticEvent,
    newInputValue: string
  ) => {
    if (selectedItem === null) {
      setInputValue(newInputValue);
    }
  };

  // Handle manual changes to the input field of modifiers
  const handleRemovedInputChange = (
    event: React.SyntheticEvent,
    newInputValue: string
  ) => {
    if (selectedRemovedItem === null) {
      setRemovedInputValue(newInputValue);
    }
  };

  const handleDeleteItem = (index: number) => {
    remove(index);
  };

  const handleDeleteRemoved = (index: number) => {
    removeRemovedItem(index);
  };

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    console.log({ data });
    const { branch_id, added_items = [], removed_items = [] } = data;
    const dataToSend = {
      branch_id: branch_id !== "dynamic" ? +branch_id : null,
      menu_id: Number(modifierId),
      items: added_items.map((item) => {
        return {
          item_id: item.item_id,
          quantity: item.item_quantity,
          methods: item.include_in,
        };
      }),
      removed_items: removed_items.map((item) => {
        return {
          item_id: item.item_id,
          quantity: item.item_quantity,
          methods: item.include_in,
        };
      }),
    };
    if (mode === "add") {
      mutate(dataToSend);
    } else {
      mutateUpdate({
        id: recipeId as string,
        ...dataToSend,
      });
    }
  };

  useEffect(() => {
    if (getRecipeStatus === "success") {
      // if (isSaving && !isFetchingRecipe) {
      console.log("resetting", { recipeData });
      reset({
        branch_id: !!recipeData.data?.branch?.id
          ? recipeData.data?.branch?.id
          : "dynamic",
        added_items: recipeData.data.menu_recipe_items.map((item: any) => {
          return {
            item_id: item.item?.id,
            item_name: item.item?.name,
            item_sku: item.item?.sku,
            item_unit: item.item?.unit?.name,
            item_quantity: item.quantity,
            item_unit_cost: item.item?.unit_cost,
            include_in: item.methods,
          };
        }),
        removed_items: recipeData.data.menu_recipe_items_removed.map(
          (item: any) => {
            return {
              item_id: item.item?.id,
              item_name: item.item?.name,
              item_sku: item.item?.sku,
              item_unit: item.item?.unit?.name,
              item_quantity: item.quantity,
              item_unit_cost: item.item?.unit_cost,
              include_in: item.methods,
            };
          }
        ),
      });
      // setIsSaving(false);
      // }
    }
  }, [getRecipeStatus]);

  //Add success and error handling
  useEffect(() => {
    if (status === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("addedSuccessfully"),
        },
      ]);
      navigateToInventory();
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors?.[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  //Update success and error handling
  useEffect(() => {
    if (statusUpdate === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("updatedSuccessfully"),
        },
      ]);
    } else if (statusUpdate === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            errorUpdate?.data.errors?.[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [statusUpdate]);

  let totalAddedItemsCost = 0;

  (watch("added_items") || []).forEach((item) => {
    totalAddedItemsCost += item.total ?? 0;
  });

  let totalRemovedItemsCost = 0;

  (watch("removed_items") || []).forEach((item) => {
    totalRemovedItemsCost += item.total ?? 0;
  });

  const selectedAddedItemsIds =
    watch("added_items")?.map((item) => item.item_id) ?? [];

  const selectedRemovedItemsIds =
    watch("removed_items")?.map((item) => item.item_id) ?? [];

  const allSelectedItemsIds = [
    ...selectedAddedItemsIds,
    ...selectedRemovedItemsIds,
  ];

  let addedCost = 0;

  watch("added_items")?.forEach((item) => {
    addedCost += (item?.item_quantity ?? 0) * (item?.item_unit_cost ?? 0);
  });

  let removedCost = 0;

  watch("removed_items")?.forEach((item) => {
    removedCost += (item?.item_quantity ?? 0) * (item?.item_unit_cost ?? 0);
  });

  const allRecipeCost = addedCost - removedCost;

  console.log({ errors });

  return (
    <Stack gap={3} p={{ xs: 2, sm: 4 }}>
      <Stack
        direction={"row"}
        spacing={"12px"}
        alignItems={"center"}
        color={"#101828"}
      >
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          width={"60px"}
          height={"60px"}
          bgcolor={"#D0D5DD"}
          borderRadius={"16px"}
        >
          <img src={InventoryIcon} width={"32px"} alt="" />
        </Box>
        <Stack>
          <Typography fontSize={"30px"} lineHeight={"38.4px"}>
            {productData?.data.name}
          </Typography>
          <Typography lineHeight={"38.4px"}>
            {t("totalCost")}:{" "}
            <strong>
              {watch("branch_id") !== "dynamic"
                ? `${allRecipeCost.toFixed(3)} SAR`
                : t("dynamic")}
            </strong>
          </Typography>
        </Stack>
      </Stack>
      {/* divider */}
      <Box bgcolor={"#F2F4F7"} width={"100%"} height={"1px"} />

      <Stack spacing={2} direction={"row"} mb={3} alignItems={"center"}>
        <Typography
          color={"#475467"}
          lineHeight={"19.2px"}
          onClick={navigateToInventory}
          sx={{ cursor: "pointer" }}
        >
          {t("inventory")}
        </Typography>
        <img src={RightArrow} alt="" />
        <Typography
          color={"#475467"}
          lineHeight={"19.2px"}
          onClick={navigateToInventory}
          sx={{ cursor: "pointer" }}
        >
          {t("modifiers")}
        </Typography>
        <img src={RightArrow} alt="" />
        <Box borderRadius={"4px"} bgcolor={"#EAECF0"} p={"4px 8px"}>
          <Typography color={"#344054"} fontWeight={600} lineHeight={"19.2px"}>
            {/* {mode === "add" ? generalT("add") : productData?.data.reference} */}
            {productData?.data.name}
          </Typography>
        </Box>
      </Stack>

      {isLoading || isLoadingRecipe ? (
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          minHeight={"250px"}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack gap={2}>
              <Stack gap={2}>
                <Typography
                  color={"#344054"}
                  fontSize={"14px"}
                  fontWeight={700}
                  lineHeight={"16.8px"}
                >
                  {generalT("branch")}
                </Typography>
                <FormControl fullWidth size="small" error={!!errors.branch_id}>
                  <InputLabel id="demo-simple-select-label">
                    {generalT("branch")}
                  </InputLabel>
                  <Controller
                    name="branch_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        key={field.value}
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label={generalT("branch")}
                        sx={{ maxWidth: "220px" }}
                      >
                        <MenuItem value={"dynamic"}>{t("dynamic")}</MenuItem>
                        {enabledBranches?.map(
                          ({ id, name, active_subscriptions }) => {
                            return (
                              <MenuItem
                                key={id}
                                value={`${id}`}
                                // disabled={
                                //   !active_subscriptions?.package.has_items
                                // }
                              >
                                {name}
                              </MenuItem>
                            );
                          }
                        )}
                      </Select>
                    )}
                  />
                  <FormHelperText id="my-helper-text">
                    {generalT(`${errors.branch_id?.message ?? ""}`)}
                  </FormHelperText>
                </FormControl>
              </Stack>

              <Typography fontSize={"14px"} fontWeight={600}>
                {t("addItemsPRO")}
              </Typography>

              {fields.map((itemField, index) => {
                const addedItemsList = watch("added_items") ?? [];
                const totalCost =
                  addedItemsList[index].item_quantity *
                  (addedItemsList[index].item_unit_cost ?? 0);
                return (
                  <Box key={itemField.id} borderRadius={1.5}>
                    <Grid
                      container
                      spacing={{ xs: 1, md: 2 }}
                      alignItems={"center"}
                    >
                      <Grid item xs={11} sm={11}>
                        <Stack
                          direction={"row"}
                          gap={{ xs: 2, md: 6.5 }}
                          alignItems={"center"}
                          flexWrap={"wrap"}
                        >
                          <Box sx={{ minWidth: 180, width: 180 }}>
                            <Typography
                              color={"#667085"}
                              fontSize={"13px"}
                              lineHeight={"24px"}
                            >
                              {t("item")}
                            </Typography>
                            <Typography
                              color={"#667085"}
                              fontSize={"13px"}
                              lineHeight={"24px"}
                              fontWeight={700}
                            >
                              {itemField.item_name} ({itemField.item_unit})
                            </Typography>
                          </Box>

                          <Box sx={{ minWidth: 80, width: 80 }}>
                            <Typography
                              color={"#667085"}
                              fontSize={"13px"}
                              lineHeight={"24px"}
                            >
                              {t("sku")}
                            </Typography>
                            <Typography
                              color={"#667085"}
                              fontSize={"13px"}
                              lineHeight={"24px"}
                              fontWeight={700}
                              whiteSpace={"nowrap"}
                            >
                              {itemField.item_sku}
                            </Typography>
                          </Box>

                          <Controller
                            name={`added_items.${index}.item_quantity`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                type="number"
                                label={`${t("qnty")}`}
                                variant="outlined"
                                {...field}
                                error={
                                  !!errors.added_items?.[index]?.item_quantity
                                }
                                helperText={
                                  errors.added_items?.[index]?.item_quantity
                                    ?.message
                                }
                                fullWidth
                                size="small"
                                sx={{ minWidth: 170, width: 170 }}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <Typography fontSize={"14px"}>
                                        {itemField.item_unit}
                                      </Typography>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            )}
                          />

                          <FormControl
                            size="small"
                            error={!!errors.added_items?.[index]?.include_in}
                          >
                            <InputLabel id="demo-multiple-checkbox-label">
                              {t("includeIn")}
                            </InputLabel>
                            <Controller
                              name={`added_items.${index}.include_in`}
                              control={control}
                              defaultValue={[]}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  size="small"
                                  sx={{ minWidth: 170, width: 170 }}
                                  labelId="demo-multiple-checkbox-label"
                                  id="demo-multiple-checkbox"
                                  multiple
                                  maxRows={2}
                                  input={
                                    <OutlinedInput label={t("includeIn")} />
                                  }
                                  renderValue={(selected) =>
                                    selected.map((value) => t(value)).join(", ")
                                  }
                                >
                                  {sellMethods.map((item) => (
                                    <MenuItem
                                      key={item.value}
                                      value={item.value}
                                    >
                                      <Checkbox
                                        size="small"
                                        checked={
                                          field.value &&
                                          field.value.indexOf(item.value) > -1
                                        }
                                      />
                                      {t(item.label)}
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            />
                            <FormHelperText id="my-helper-text">
                              {errors.added_items?.[index]?.include_in?.message}
                            </FormHelperText>
                          </FormControl>
                          {/* calculations */}
                          <CalcItem
                            label={t("unitCost")}
                            value={`${
                              watch("branch_id") !== "dynamic"
                                ? itemField.item_unit_cost?.toFixed(3) ?? 0
                                : t("dynamic")
                            }`}
                          />
                          <CalcItem
                            label={t("total")}
                            value={`${
                              watch("branch_id") !== "dynamic"
                                ? totalCost?.toFixed(3) ?? 0
                                : t("dynamic")
                            }`}
                          />
                        </Stack>
                      </Grid>
                      <Grid item xs={1} md={1}>
                        <Box
                          display={"flex"}
                          justifyContent={"flex-end"}
                          alignItems={"center"}
                          sx={{ flexGrow: 1 }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => {
                              handleDeleteItem(index);
                            }}
                          >
                            <img
                              src={DeleteIcon}
                              alt=""
                              width={"16px"}
                              style={{ cursor: "pointer" }}
                            />
                          </IconButton>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                );
              })}
              <Autocomplete
                size="small"
                disablePortal
                id="combo-box-demo"
                options={
                  itemsData?.data.filter(
                    (item: any) => !allSelectedItemsIds?.includes(item.id)
                  ) ?? []
                }
                sx={{ width: 255 }}
                getOptionLabel={(option: any) => option.name}
                getOptionKey={(option) => `${option.id}`}
                value={selectedItem}
                onChange={handleItemSelect}
                inputValue={inputValue}
                onInputChange={handleInputChange}
                renderInput={(params: any) => (
                  <TextField {...params} label={t("searchAndAddItem")} />
                )}
                loading={isFetchingItems}
                noOptionsText={
                  !watch("branch_id")
                    ? generalT("selectBranchFirst")
                    : generalT("noOption")
                }
              />

              <Typography fontSize={"14px"} fontWeight={600}>
                {t("removeItemsPRO")}
              </Typography>

              {/* removed items */}
              {removedItemsFields.map((itemField, index) => {
                const removedItemsList = watch("removed_items") ?? [];
                const totalCost =
                  removedItemsList[index].item_quantity *
                  (removedItemsList[index].item_unit_cost ?? 0);
                return (
                  <Box key={itemField.id} borderRadius={1.5}>
                    <Grid
                      container
                      spacing={{ xs: 1, md: 2 }}
                      alignItems={"center"}
                    >
                      <Grid item xs={11} sm={11}>
                        <Stack
                          direction={"row"}
                          gap={{ xs: 2, md: 6.5 }}
                          alignItems={"center"}
                          flexWrap={"wrap"}
                        >
                          <Box sx={{ minWidth: 180, width: 180 }}>
                            <Typography
                              color={"#667085"}
                              fontSize={"13px"}
                              lineHeight={"24px"}
                            >
                              {t("item")}
                            </Typography>
                            <Typography
                              color={"#667085"}
                              fontSize={"13px"}
                              lineHeight={"24px"}
                              fontWeight={700}
                            >
                              {itemField.item_name} ({itemField.item_unit})
                            </Typography>
                          </Box>

                          <Box sx={{ minWidth: 80, width: 80 }}>
                            <Typography
                              color={"#667085"}
                              fontSize={"13px"}
                              lineHeight={"24px"}
                            >
                              {t("sku")}
                            </Typography>
                            <Typography
                              color={"#667085"}
                              fontSize={"13px"}
                              lineHeight={"24px"}
                              fontWeight={700}
                              whiteSpace={"nowrap"}
                            >
                              {itemField.item_sku}
                            </Typography>
                          </Box>

                          <Controller
                            name={`removed_items.${index}.item_quantity`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                type="number"
                                label={`${t("qnty")}`}
                                variant="outlined"
                                {...field}
                                error={
                                  !!errors.removed_items?.[index]?.item_quantity
                                }
                                helperText={
                                  errors.removed_items?.[index]?.item_quantity
                                    ?.message
                                }
                                fullWidth
                                size="small"
                                sx={{ minWidth: 170, width: 170 }}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <Typography fontSize={"14px"}>
                                        {itemField.item_unit}
                                      </Typography>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            )}
                          />

                          <FormControl
                            size="small"
                            error={!!errors.removed_items?.[index]?.include_in}
                          >
                            <InputLabel id="demo-multiple-checkbox-label">
                              {t("includeIn")}
                            </InputLabel>
                            <Controller
                              name={`removed_items.${index}.include_in`}
                              control={control}
                              defaultValue={[]}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  size="small"
                                  sx={{ minWidth: 170, width: 170 }}
                                  labelId="demo-multiple-checkbox-label"
                                  id="demo-multiple-checkbox"
                                  multiple
                                  maxRows={2}
                                  input={
                                    <OutlinedInput label={t("includeIn")} />
                                  }
                                  renderValue={(selected) =>
                                    selected.map((value) => t(value)).join(", ")
                                  }
                                >
                                  {sellMethods.map((item) => (
                                    <MenuItem
                                      key={item.value}
                                      value={item.value}
                                    >
                                      <Checkbox
                                        size="small"
                                        checked={
                                          field.value &&
                                          field.value.indexOf(item.value) > -1
                                        }
                                      />
                                      {t(item.label)}
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            />
                            <FormHelperText id="my-helper-text">
                              {
                                errors.removed_items?.[index]?.include_in
                                  ?.message
                              }
                            </FormHelperText>
                          </FormControl>
                          {/* calculations */}
                          <CalcItem
                            label={t("unitCost")}
                            value={`${
                              itemField.item_unit_cost?.toFixed(3) ?? 0
                            }`}
                          />
                          <CalcItem
                            label={t("total")}
                            value={`${totalCost.toFixed(3)}`}
                          />
                        </Stack>
                      </Grid>
                      <Grid item xs={1} md={1}>
                        <Box
                          display={"flex"}
                          justifyContent={"flex-end"}
                          alignItems={"center"}
                          sx={{ flexGrow: 1 }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => {
                              handleDeleteRemoved(index);
                            }}
                          >
                            <img
                              src={DeleteIcon}
                              alt=""
                              width={"16px"}
                              style={{ cursor: "pointer" }}
                            />
                          </IconButton>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                );
              })}

              <Autocomplete
                size="small"
                disablePortal
                id="combo-box-demo"
                options={
                  itemsData?.data.filter(
                    (item: any) => !allSelectedItemsIds?.includes(item.id)
                  ) ?? []
                }
                sx={{ width: 255 }}
                getOptionLabel={(option: any) => option.name}
                getOptionKey={(option) => `${option.id}`}
                value={selectedRemovedItem}
                onChange={handleRemovedItemSelect}
                inputValue={removedInputValue}
                onInputChange={handleRemovedInputChange}
                renderInput={(params: any) => (
                  <TextField {...params} label={t("searchAndAddItem")} />
                )}
                loading={isFetchingItems}
                noOptionsText={
                  !watch("branch_id")
                    ? generalT("selectBranchFirst")
                    : generalT("noOption")
                }
              />
            </Stack>

            <Stack
              spacing={2}
              direction={"row"}
              mt={3}
              justifyContent={"flex-end"}
            >
              <Button
                variant="outlined"
                color="tertiary"
                onClick={navigateToInventory}
                sx={{ minWidth: "180px" }}
              >
                {generalT("back")}
              </Button>

              <LoadingButton
                variant="contained"
                type="submit"
                loading={isPendingUpdate || isPending}
                sx={{ minWidth: "180px" }}
              >
                {generalT("save")}
              </LoadingButton>
            </Stack>
          </form>
        </>
      )}
    </Stack>
  );
};

export default ModifierRecipe;
